import {
  NavigationGuardNext,
  RouteLocationNormalized,
  useRoute,
  useRouter,
} from 'vue-router';
import { computed } from 'vue';

import { EAppState, ROUTE_TYPE } from '@/constants';
import { logger } from '@/utils';
import { isRouteSectionChange } from '@/utils/isRouteSectionChange';
import { useSearchStore } from '@/stores/search';
import { checkSearchStateAndResetPagination } from '@/domains/stores/pagination/checkSearchStateAndResetPagination';
import { useTenantsStore } from '@/stores/tenants';
import useTenantServices from '@/composables/application/useTenantServices';
import { useAppStore } from '@/stores/app';
import redirectToInitialRoute from '@/domains/redirectToInitialRoute';

export const useAppGeneralNavigation = () => {
  const tenantsStore = useTenantsStore();
  const router = useRouter();
  const route = useRoute();
  const appStore = useAppStore();

  const currentAppState = computed(() => appStore.appState);
  const entryUid = computed(() => appStore.UTMSourceParams?.entryUid);
  const isAuthByEntryCode = computed(() => appStore.isAuthByEntryCode);

  const redirectPathToSignIn = computed(() => (isAuthByEntryCode.value
    ? `/contractor_personal_account/sign_in?entry_uid=${entryUid.value}`
    : '/sign_in'));

  const subscribeRouterGuards = () => router.beforeEach((to, from, next) => {
    if (currentAppState.value === EAppState.authenticated) {
      if (to.meta.type === ROUTE_TYPE.unauthenticated) {
        logger.log('[Router Guard] Navigate to /');
        router.push('/');
        return;
      }
    } else if (currentAppState.value !== EAppState.checking && to.meta.type === ROUTE_TYPE.private) {
      logger.log('[Router Guard] Navigate to sign_in');
      // если не авторизован и находится на private роуте, редиректим на Регистрацию
      if (isAuthByEntryCode.value) {
        router.push(`/contractor_personal_account/sign_in?entry_uid=${entryUid.value}`);
      } else {
        router.push('/sign_in');
      }
      return;
    }
    /**
       * Т.к. в next() описан warn(`The "next" callback was called more than once in one navigation
       * guard when going from "${from.fullPath}" to "${to.fullPath}". It should be called exactly
       * one time in each navigation guard. This will fail in production.`)
       * добавляем проверку
       */
    if (from.fullPath !== to.fullPath) {
      next();
    }
  });

  const checkRouteSectionChangeAndResetStore = () => router.beforeEach((
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    const searchStore = useSearchStore();

    if (isRouteSectionChange(from, to) && searchStore.hasSearchQueries()) {
      checkSearchStateAndResetPagination();

      searchStore.$reset();
    }

    next();
  });

  const initializeRouter = () => router.isReady()
    .then(subscribeRouterGuards)
    .then(checkRouteSectionChangeAndResetStore)
    .catch(() => {});

  const isRouteUnauthorized = (appRoute) => appRoute?.meta?.type === ROUTE_TYPE.unauthenticated;
  const isRoutePublic = (appRoute) => appRoute?.meta?.type === ROUTE_TYPE.public;
  const isAdminRoute = (appRoute) => appRoute?.meta?.type === ROUTE_TYPE.admin;
  const isPrivateRoute = (appRoute) => appRoute?.meta?.type === ROUTE_TYPE.private;
  const isMainRoute = (appRoute) => appRoute?.path === '/';
  const isInvitationAcceptanceRoute = (appRoute) => appRoute?.name === 'invitationAcceptance';

  const commonRouteHandler = async () => {
    const tenants = tenantsStore.userTenants || [];
    const { currentTenant } = tenantsStore;

    /** Если роут для принятия приглашения к подключению к TMS (переход по ссылке из письма) */
    if (isInvitationAcceptanceRoute(route)) return;

    if (tenants.length && currentTenant) {
      /**
       * Сбрасываем состояние загрузки приложения (к этому моменту все необходимые данные подгружены),
       * так как в противном случае router.isReady не разрешится и приложение зависнет
       */
      appStore.setLoadingState(false);

      /**
       * Для корректной проверки раута ниже (if isMainRoute...), ждем когда router выполнить необходимые действия.
       * Одно из которых переход к определенному рауту. В текущем месте актуально при обновлении страницы.
       * Как пример (если убрать ожидание), находились на '/employees', обновили страницу, route.fullPath будет '/' и нас перекинет на дефолтный URL.
       * */
      await router.isReady();

      if (isMainRoute(route)) {
        redirectToInitialRoute();
      }
    } else {
      router.push('/').then(() => {
        appStore.setLoadingState(false);
      });
    }
  };

  const { updateTenantData } = useTenantServices();

  const updateTenantAndNavigateToRoute = async () => {
    await updateTenantData();
    await commonRouteHandler();
  };

  const navigateAfterLogout = () => {
    if (isPrivateRoute(route)) {
      const routeBeforeLogout = !isMainRoute(route) ? route.fullPath : null;
      appStore.setRouteBeforeLogout(routeBeforeLogout);
    }
    router.push(redirectPathToSignIn.value);
  };

  const navigateToMaintenance = () => {
    router.push('/maintenance');
  };

  const navigateAfterFailedAuthentication = async () => {
    await router.isReady().catch(() => {});
    if (!isRouteUnauthorized(route) && !isRoutePublic(route)) {
      navigateAfterLogout();
    }
  };

  return {
    redirectPathToSignIn,

    initializeRouter,
    updateTenantAndNavigateToRoute,
    navigateAfterLogout,
    navigateToMaintenance,
    navigateAfterFailedAuthentication,
    isPrivateRoute,
    isAdminRoute,
  };
};
